import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import './CategoryPageTemplate.css';

/* ─────────────────────────────────────────────────────────────
   FALLBACK SVG — broken image pe dikhega
───────────────────────────────────────────────────────────── */
const FALLBACK_SVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3ede3'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='48' fill='%23c9a96e'%3E%F0%9F%AA%94%3C/text%3E%3C/svg%3E`;

/* ─────────────────────────────────────────────────────────────
   GLOBAL IMAGE CACHE
───────────────────────────────────────────────────────────── */
const imageCache = new Set();

/* ─────────────────────────────────────────────────────────────
   SAFE STRING HELPERS
   Object hone par bhi crash na ho
───────────────────────────────────────────────────────────── */
function safeString(val) {
  if (!val && val !== 0) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (Array.isArray(val)) {
    return val
      .map((v) => {
        if (typeof v === 'object' && v !== null) {
          // { size: '100g', mrp: '₹80' } format
          return v.size ? `${v.size}${v.mrp ? ` (${v.mrp})` : ''}` : JSON.stringify(v);
        }
        return String(v);
      })
      .join(' / ');
  }
  if (typeof val === 'object' && val !== null) {
    // single packing object
    return val.size ? `${val.size}${val.mrp ? ` (${val.mrp})` : ''}` : '';
  }
  return '';
}

/* ─────────────────────────────────────────────────────────────
   PRELOAD HELPERS
───────────────────────────────────────────────────────────── */
function preloadImage(src) {
  if (!src || imageCache.has(src)) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload  = () => { imageCache.add(src); resolve(); };
    img.onerror = () => resolve();
    img.src = src;
  });
}

function preloadBatch(srcs = []) {
  return Promise.all(srcs.filter(Boolean).map(preloadImage));
}

/* ─────────────────────────────────────────────────────────────
   PRODUCT IMAGE COMPONENT
───────────────────────────────────────────────────────────── */
function ProductImage({ src, alt, priority = false }) {
  const isCached   = imageCache.has(src);
  const [loaded,   setLoaded]   = useState(isCached);
  const [error,    setError]    = useState(false);
  const [inView,   setInView]   = useState(priority || isCached);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (inView) return;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '400px 0px', threshold: 0 }
    );
    const el = wrapperRef.current;
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView || !src || loaded) return;
    if (imageCache.has(src)) { setLoaded(true); return; }
    const img = new Image();
    img.onload  = () => { imageCache.add(src); setLoaded(true); };
    img.onerror = () => { setError(true); setLoaded(true); };
    img.src = src;
  }, [inView, src, loaded]);

  return (
    <div ref={wrapperRef} className="p-img-wrapper">
      {!loaded && <div className="p-img-skeleton" aria-hidden="true" />}
      {inView && !error && (
        <img
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'low'}
          decoding={priority ? 'sync' : 'async'}
          onLoad={() => { imageCache.add(src); setLoaded(true); }}
          onError={() => { setError(true); setLoaded(true); }}
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', display: 'block',
            willChange: 'transform',
            opacity:    loaded ? 1 : 0,
            filter:     loaded ? 'blur(0px) brightness(1.03)' : 'blur(6px)',
            transform:  loaded ? 'scale(1) translateZ(0)' : 'scale(1.03) translateZ(0)',
            transition: loaded
              ? 'opacity 0.55s ease, filter 0.55s ease, transform 0.55s ease'
              : 'none',
          }}
        />
      )}
      {error && (
        <img
          src={FALLBACK_SVG} alt={alt}
          style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN TEMPLATE
───────────────────────────────────────────────────────────── */
export default function CategoryPageTemplate({
  title,
  subtitle,
  products,
  heroBg,
  typeIcon,
  seoDesc,
  tiers,
  extraTags,
}) {
  const firstAvailable = products.some((p) => p.tier === tiers[0]?.id)
    ? tiers[0].id
    : 'All';

  const [activeTier, setActiveTier] = useState(firstAvailable);
  const preloadedTiers = useRef(new Set());

  const availableTiers = tiers.filter(
    (t) => t.id === 'All' || products.some((p) => p.tier === t.id)
  );

  useReveal();

  useEffect(() => {
    const visible = firstAvailable === 'All'
      ? products
      : products.filter((p) => p.tier === firstAvailable);
    preloadBatch(visible.slice(0, 6).map((p) => p.img));
    preloadedTiers.current.add(firstAvailable);
    setTimeout(() => { preloadBatch(visible.slice(6, 12).map((p) => p.img)); }, 500);
    setTimeout(() => {
      preloadBatch(products.map((p) => p.img));
      tiers.forEach((t) => preloadedTiers.current.add(t.id));
    }, 1500);
  }, []); // eslint-disable-line

  const handleTierChange = useCallback((tierId) => {
    setActiveTier(tierId);
    if (preloadedTiers.current.has(tierId)) return;
    preloadedTiers.current.add(tierId);
    const tierProds = tierId === 'All' ? products : products.filter((p) => p.tier === tierId);
    preloadBatch(tierProds.slice(0, 6).map((p) => p.img));
    setTimeout(() => preloadBatch(tierProds.slice(6).map((p) => p.img)), 300);
  }, [products]);

  const handleTierHover = useCallback((tierId) => {
    if (preloadedTiers.current.has(tierId)) return;
    preloadedTiers.current.add(tierId);
    const tierProds = tierId === 'All' ? products : products.filter((p) => p.tier === tierId);
    preloadBatch(tierProds.map((p) => p.img));
  }, [products]);

  const filtered = activeTier === 'All'
    ? products
    : products.filter((p) => p.tier === activeTier);

  return (
    <main style={{ paddingTop: '70px' }}>

      {/* ── HERO ── */}
      <section
        className="cat-hero"
        style={{ backgroundImage: `url('${heroBg}')` }}
        aria-label={title}
      >
        <div className="cat-hero__overlay" />
        <div className="cat-hero__content">
          <p className="cat-hero__icon">{typeIcon}</p>
          <h1 className="cat-hero__title">{title}</h1>
          <p className="cat-hero__sub">{subtitle}</p>
        </div>
      </section>

      {/* ── TIER FILTER ── */}
      <section className="cat-filter-section">
        <div className="container">
          <div className="tier-filter-bar">
            {availableTiers.map((t) => (
              <button
                key={t.id}
                className={`tier-pill ${activeTier === t.id ? 'active' : ''}`}
                onClick={() => handleTierChange(t.id)}
                onMouseEnter={() => handleTierHover(t.id)}
                onFocus={() => handleTierHover(t.id)}
              >
                {t.badge && <span className="pill-new-badge">{t.badge}</span>}
                <span className="pill-icon">{t.icon}</span>
                <span>{t.label}</span>
                {t.desc && <span className="pill-desc">{t.desc}</span>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS GRID ── */}
      <section className="section" style={{ paddingTop: '2rem' }}>
        <div className="container">
          {filtered.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
              No products found in this category.
            </p>
          ) : (
            <div className="cat-products-grid">
              {filtered.map((p, i) => {
                const isPriority = i < 6;

                /* ── Safe values — object hone par bhi crash nahi ── */
                const burnTimeStr = safeString(p.burnTime || p.duration);
                const weightStr   = safeString(p.weight);
                const fragranceStr = safeString(p.fragrance);
                const typeStr      = safeString(p.type);

                return (
                  <div
                    key={p.id}
                    className="p-card reveal"
                    style={{ transitionDelay: `${Math.min(i, 8) * 0.07}s` }}
                  >
                    <Link to={`/products/${p.id}`}>
                      <div className="p-card-img">
                        <ProductImage
                          src={p.img}
                          alt={`${p.name} — ${fragranceStr}`}
                          priority={isPriority}
                        />
                        <div className="p-card-tier">{p.tier}</div>
                        <div className="p-card-type-badge">{typeStr}</div>

                        {extraTags && (
                          <div className="p-card-extra-tags">
                            {extraTags.bottomLeft && (
                              <span className="p-card-extra-tag p-card-extra-tag--left">
                                {extraTags.bottomLeft}
                              </span>
                            )}
                            {extraTags.bottomRight && (
                              <span className="p-card-extra-tag p-card-extra-tag--right">
                                {extraTags.bottomRight}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="p-card-body">
                        <p className="p-card-frag">{typeIcon} {fragranceStr}</p>
                        <h3 className="p-card-name">{p.name}</h3>
                        <div className="p-card-meta">
                          {burnTimeStr && <span>⏱ {burnTimeStr}</span>}
                          {weightStr   && <span>⚖️ {weightStr}</span>}
                        </div>
                      </div>
                    </Link>

                    <div className="p-card-actions">
                      <Link to={`/products/${p.id}`} className="btn btn-outline-cat">
                        View Details
                      </Link>
                      <a
                        href={p.flipkartUrl || 'https://www.flipkart.com'}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-flipkart"
                      >
                        🛒 Buy on Flipkart
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ── WHY SECTION ── */}
      <section className="cat-why-section">
        <div className="cat-why__bg" />
        <div className="container cat-why__inner">
          <h3 className="reveal">Why Choose Prakhyat {title.split(' ')[0]}?</h3>
          <div className="cat-why-grid">
            {[
              'Natural Ingredients used',
              'Hand crafted by experts',
              'Pan India Availability ',
              'Customizes products for Exports, Institutions,  Temples, etc',
            ].map((w, i) => (
              <div
                key={i}
                className="cat-why-item reveal"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <span>✦</span> {w}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="cat-bottom-cta">
        <div className="container">
          <div className="cbc-inner reveal">
            <div>
              <h3>Need Customized or Bulk Supply Agarbattis</h3>
              <p>
                We supply customized products as per choice for Exports, Institutions, Temples, Corporates, etc on a Volume base pricing
              </p>
            </div>
            <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', flexShrink:0 }}>
              <Link to="/contact" className="btn btn-gold">
                📦 Contact Now
              </Link>
              <a
                href="https://wa.me/919320905751"
                className="btn btn-green"
                target="_blank"
                rel="noreferrer"
              >
                💬 WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
